//
// Copyright 2024 DXOS.org
//

import {
  next as A,
  type Doc as AutomergeDoc,
  type ChangeFn,
  type ChangeOptions,
  type Heads,
} from '@automerge/automerge';
import { type DocHandleChangePayload } from '@automerge/automerge-repo';
import * as Schema from 'effect/Schema';
import type { InspectOptionsStylized, inspect } from 'util';

import { type CleanupFn, Event } from '@dxos/async';
import { inspectCustom } from '@dxos/debug';
import { type Entity, type Type } from '@dxos/echo';
import {
  DATA_NAMESPACE,
  type DatabaseDirectory,
  EncodedReference,
  type EntityStructure,
  isEncodedReference,
} from '@dxos/echo-protocol';
import { EntityKind, type EntityMeta, getStrongDependencyUris } from '@dxos/echo/internal';
import { isProxy } from '@dxos/echo/internal';
import { assertArgument, invariant } from '@dxos/invariant';
import { EID, EntityId, type SpaceId, URI } from '@dxos/keys';
import { log } from '@dxos/log';
import { ComplexMap, defer, getDeep, setDeep, throwUnhandledError } from '@dxos/util';

import * as Doc from '../automerge/Doc.ts';
import { type DocHandleProxy } from '../automerge/index.ts';
import { docChangeSemaphore } from './doc-semaphore.ts';
import { type DecodedAutomergePrimaryValue, type GetObjectCoreByIdOptions, TargetKey } from './types.ts';

/**
 * Minimal interface that ObjectCore requires from the containing database.
 * Kept in this file (rather than types.ts) to avoid a circular import:
 * types.ts must not reference ObjectCore.
 * @internal
 */
export interface IDatabaseBinding {
  readonly spaceId: SpaceId;
  getObjectCoreById(id: string, opts?: GetObjectCoreByIdOptions): ObjectCore | undefined;
  /** The snapshot core `id` is now bound to a document: its passive handle is done with unless it is that document. */
  endSnapshot(id: string): void;
}

// Strings longer than this will have collaborative editing disabled for performance reasons.
// TODO(dmaretskyi): Remove in favour of explicitly specifying this in the API/Schema.
const STRING_CRDT_LIMIT = 300_000;

export const META_NAMESPACE = 'meta';
const SYSTEM_NAMESPACE = 'system';

/**
 * Per-document `getUpdatedAt` cache, keyed by heads: inline objects that share a document
 * (e.g. everything nested under the space root) would otherwise repeat the same backend
 * lookup for every object on every call.
 */
const updatedAtCache = new WeakMap<AutomergeDoc<unknown>, { heads: string; updatedAt: number | undefined }>();

export type ObjectCoreOptions = {
  type?: EncodedReference;
  meta?: EntityMeta;
  immutable?: boolean;
};

/** An object shown from the index, over its document's passive handle, whose heads it shows. */
export type ObjectSnapshot = {
  root: { objects: Record<string, EntityStructure> };
  updatedAt: number | undefined;
  handle: DocHandleProxy<DatabaseDirectory>;
};

export type SnapshotState = Omit<ObjectSnapshot, 'root' | 'handle'> & {
  structure: EntityStructure;
  heads: Heads;
  bytes: Uint8Array;
  documentId: string;
};

/**
 * The single key a write is touching, so the proxy targets are refreshed for that key alone.
 * @internal
 */
export type TargetRefreshScope = { target: object; key: string };

/**
 * {@link ObjectCore.isDeleted} verdicts by remaining depth, valid only while no core changes — that is,
 * within one synchronous read.
 */
export type DeletionMemo = Map<ObjectCore, boolean[]>;

/** Dependency lookups during a deletion walk; shared so the walk allocates no options per hop. */
const LOOKUP_LOADING: GetObjectCoreByIdOptions = { load: true };
const LOOKUP_HELD: GetObjectCoreByIdOptions = { load: false };

/**
 *
 */
// TODO(burdon): Comment.
export class ObjectCore {
  // TODO(dmaretskyi): Start making some of those fields private.
  // TODO(dmaretskyi): Create a discriminated union for the bound/not bound states.

  /**
   * Id of the ECHO object.
   */
  public id = EntityId.random();

  /**
   * Set when the object is bound to a database.
   */
  public entityManager?: IDatabaseBinding | undefined;

  /**
   * Set when the object is not bound to a database.
   */
  public doc?: AutomergeDoc<EntityStructure> | undefined;

  /**
   * Set when the object is bound to a database.
   */
  public docHandle?: DocHandleProxy<DatabaseDirectory> = undefined;

  #snapshot?: ObjectSnapshot = undefined;

  /**
   * The system fields a deletion walk reads, decoded at {@link #deletionRoot}: each document read is a
   * call into wasm, and a write replaces the root. Binding and remounting replace it too.
   */
  #deletionRoot: unknown = undefined;
  #ownDeleted = false;
  /** Same-space strong dependencies, whose deletion deletes this entity. */
  #parentId: EntityId | undefined = undefined;
  #sourceId: EntityId | undefined = undefined;
  #targetId: EntityId | undefined = undefined;
  #mergedIntoId: EntityId | undefined = undefined;

  /** When the document last loaded or changed (`performance.now()`); the space releases it after a TTL. */
  public activeAt = 0;

  /** When this client last wrote to the document (`performance.now()`), or 0. */
  public editedAt = 0;

  /** Change listeners attached through doc accessors: an editor or store adapter holding the document. */
  #accessorListeners = 0;

  /**
   * Key path at where we are mounted in the `doc` or `docHandle`.
   * The value at path must be of type `EntityStructure`.
   */
  public mountPath: Doc.KeyPath = [];

  /**
   * The branch this core instance is bound to (`'main'` = the canonical document). Set on switch
   * (device-global canonical) and on `db.branch` bindings (independent instances), so the branch is
   * a property of the instance — `Obj.getBranch(obj)` reads it. Distinct from the device-global
   * current-branch selection (persisted separately), which a `db.branch` instance overrides locally.
   */
  public branch: string = 'main';

  /**
   * Handles link resolution as well as manual changes.
   * Fires on real data changes (local writes, remote sync) and backs the subscription channel.
   */
  public readonly updates = new Event();

  /**
   * Re-fills every proxy target of this core from the document. Installed by the proxy handler once a
   * root proxy exists; called ahead of every update notification, and directly after a write to a
   * bound document, so a target never serves a value the document no longer holds even when the DB
   * does not route the change event back to this core.
   */
  public refreshTargets: ((scope?: TargetRefreshScope) => void) | undefined;

  /** Narrows the refresh while a {@link changeTargetKey} scope is open. */
  #refreshScope: TargetRefreshScope | undefined;

  /**
   * Runs `fn` — a write of exactly `key` on `target` — with the refresh narrowed to that one key. The
   * unnarrowed refresh re-reads every key of the record, which would make a single-property write cost
   * the width of the object. Narrowed rather than deferred because the write notifies subscribers
   * synchronously, and they must already see the new value.
   */
  changeTargetKey<T>(target: object, key: string, fn: () => T): T {
    const previous = this.#refreshScope;
    this.#refreshScope = { target, key };
    try {
      return fn();
    } finally {
      this.#refreshScope = previous;
    }
  }

  /**
   * Set by {@link #refresh} while a bound write is in flight, so the write can tell whether the DB
   * already routed the change event back to this core and refreshed from it.
   */
  #refreshedDuringWrite = false;

  #refresh(): void {
    this.#refreshedDuringWrite = true;
    this.refreshTargets?.(this.#refreshScope);
  }

  /**
   * Run a write against the bound document, refreshing afterwards only if the DB did not route the
   * change event back to this core and refresh from it (it does for a routed core, synchronously,
   * inside `write`). A core bound to a branch, or dropped from the DB's `_objects`, stays bound but
   * unrouted and would otherwise never refresh — so this cannot simply be dropped.
   *
   * The second pass was not merely redundant: for a container write `_writeThrough` sees the proxy
   * the first pass installed, returns false, and falls through to a refresh of the object's full
   * width with the narrowing scope already closed.
   */
  #writeAndRefresh<T>(write: () => T): T {
    this.#refreshedDuringWrite = false;
    try {
      return write();
    } finally {
      const refreshed = this.#refreshedDuringWrite;
      this.#refreshedDuringWrite = false;
      if (!refreshed) {
        this.#refresh();
      }
    }
  }

  // -------------------------------------------------------------------------
  // Fields merged from ObjectInternals (formerly echo-proxy-target.ts).
  // The EchoDatabase reference is typed `unknown` here to avoid a circular dep
  // between core-db ← proxy-db; echo-handler casts it at the usage sites.
  // -------------------------------------------------------------------------

  /**
   * EchoDatabase reference. Set by echo-handler on db.add(); typed narrowly
   * at usage sites in echo-handler to avoid a circular dep with proxy-db.
   */
  public database: unknown = undefined;

  /**
   * Caching proxy targets by key path (record | array subtargets).
   * Value typed as `object` to avoid importing ProxyTarget from echo-handler.
   */
  public targetsMap = new ComplexMap<TargetKey, object>(TargetKey.hash);

  /**
   * Until an object is persisted in the database, linked object references
   * are held in this cache. Cleared on db.add().
   */
  public linkCache: Map<string, Entity.Unknown> | undefined = new Map<string, Entity.Unknown>();

  /** Cleanup functions registered by the proxy layer. */
  public subscriptions: CleanupFn[] = [];

  /**
   * `Type.AnyEntity` of the root object. Populated when a Type entity is
   * available (e.g. created via `Obj.make(SomeType, ...)`); undefined for
   * objects created without a typed schema.
   */
  public rootSchema?: Type.AnyEntity = undefined;

  /**
   * Memoized rebuilt Effect Schema for persisted `Type.Type` entities.
   * Lazily populated; invalidated when the entity's `jsonSchema` changes.
   */
  public cachedStaticSlot?: Schema.Codec<any, any> = undefined;

  /**
   * The single root proxy for this core. Replaces the `DatabaseImpl._rootProxies`
   * Map; set on first proxy construction and on db.add().
   */
  public rootProxy?: Entity.Unknown = undefined;

  toString(): string {
    return `ObjectCore { id: ${this.id} }`;
  }

  [inspectCustom](depth: number, options: InspectOptionsStylized, inspectFn: typeof inspect): string {
    return `ObjectCore ${inspectFn({ id: this.id }, options)}`;
  }

  /**
   * Create local doc with initial state from this object.
   */
  initNewObject(initialProps?: unknown, opts?: ObjectCoreOptions): void {
    invariant(!this.docHandle && !this.doc);

    initialProps ??= {};

    this.doc = A.from<EntityStructure>({
      data: this.encode(initialProps),
      meta: this.encode({
        ...opts?.meta,
        keys: opts?.meta?.keys ?? [],
        tags: opts?.meta?.tags ?? [],
        annotations: opts?.meta?.annotations ?? {},
      }),
      system: { createdAt: Date.now() },
    });
  }

  bind(options: BindOptions): void {
    // When loading existing documents, wait for the document to be ready.
    // When creating new documents (assignFromLocalState), the local doc is immediately usable.
    invariant(options.assignFromLocalState || options.docHandle.isLoaded());
    this.entityManager = options.db;
    this.docHandle = options.docHandle;
    this.mountPath = options.path;
    if (this.#snapshot) {
      this.#snapshot = undefined;
      options.db.endSnapshot(this.id);
    }
    this.activeAt = performance.now();

    const doc = this.doc;
    this.doc = undefined;

    if (options.assignFromLocalState) {
      invariant(doc, 'assignFromLocalState');

      // Prevent recursive change calls.
      using _ = defer(docChangeSemaphore(this.#writeLockKey));

      this.docHandle.change((newDoc: DatabaseDirectory) => {
        setDeep(newDoc, this.mountPath, doc);
      });
    }

    this.notifyUpdate();
  }

  get snapshot(): ObjectSnapshot | undefined {
    return this.#snapshot;
  }

  /** The handle of the document this core reads and writes: the bound one, or its snapshot's passive one. */
  get handle(): DocHandleProxy<DatabaseDirectory> | undefined {
    return this.docHandle ?? this.#snapshot?.handle;
  }

  /** The Automerge document; a snapshot core's handle loads it, and the database binds the core to it. */
  getDoc(): AutomergeDoc<unknown> {
    if (this.doc) {
      return this.doc;
    }
    const handle = this.handle;
    if (handle) {
      return handle.doc();
    }
    throw new Error('Invalid ObjectCore state');
  }

  /** Whether something holds the document through an accessor (an editor, a store adapter). */
  get hasAccessorListeners(): boolean {
    return this.#accessorListeners > 0;
  }

  /**
   * Moves a bound core back onto a snapshot of its document as it is now; the proxy and its subscribers
   * stay, and the next write or document read loads the document again.
   */
  demote(snapshot: ObjectSnapshot): void {
    invariant(this.docHandle && !this.doc, 'Only a core bound to a document is demoted.');
    this.docHandle = undefined;
    this.#snapshot = snapshot;
    // Same values, so the targets are rebuilt from the snapshot without notifying anyone.
    this.#reportingErrors(() => this.#refresh());
  }

  /** The record `mountPath` is rooted in, read without loading a snapshot's document. */
  #root(): AutomergeDoc<unknown> {
    const root = this.doc ?? this.#snapshot?.root ?? this.docHandle?.doc();
    if (root === undefined) {
      throw new Error('Invalid ObjectCore state');
    }
    return root;
  }

  /** Shows the object from the index, or shows newer index state; the core must be unbound. */
  setSnapshot(snapshot: ObjectSnapshot): void {
    invariant(!this.doc && !this.docHandle, 'Only an unbound core is backed by a snapshot.');
    this.#snapshot = snapshot;
    this.mountPath = ['objects', this.id];
  }

  /** Runs `fn` as one write scope on this core's document; see {@link DocHandleProxy.batch}. */
  batch<R>(fn: () => R): R {
    const handle = this.handle;
    return handle ? handle.batch(fn) : fn();
  }

  get hasDoc(): boolean {
    return this.doc != null || this.docHandle != null || this.#snapshot != null;
  }

  /**
   * Whether the bound document carries this object's body. A linked document settles ready while
   * still empty when the peer holding it has replicated the space directory ahead of the payload,
   * and the core exists from that moment on so the object's identity survives the body arriving.
   *
   * Derived from the document instead of stored, so it cannot disagree with what a read would find.
   */
  get isBodyAvailable(): boolean {
    return this.hasDoc && getDeep(this.#root(), this.mountPath) != null;
  }

  /** Undefined until the body arrives — see {@link isBodyAvailable}. */
  getObjectStructure(): EntityStructure | undefined {
    return getDeep<EntityStructure>(this.#root(), this.mountPath);
  }

  get #writeLockKey(): object {
    return this.handle ?? this;
  }

  /**
   * Do not take into account mountPath.
   */
  change(changeFn: ChangeFn<any>, options?: A.ChangeOptions<any>): void {
    // Prevent recursive change calls.
    using _ = defer(docChangeSemaphore(this.#writeLockKey));

    if (this.doc) {
      if (options) {
        this.doc = A.change(this.doc!, options, changeFn);
      } else {
        this.doc = A.change(this.doc!, changeFn);
      }

      // No change event is emitted here since we are not using the doc handle. Notify listeners manually.
      this.notifyUpdate();
    } else {
      const handle = this.handle;
      invariant(handle);
      // No manual notification: the DB already processes the `change` event.
      this.#writeAndRefresh(() => handle.change(changeFn, options));
    }
  }

  /**
   * Do not take into account mountPath.
   */
  changeAt(heads: Heads, callback: ChangeFn<any>, options?: ChangeOptions<any>): Heads | undefined {
    // Prevent recursive change calls.
    using _ = defer(docChangeSemaphore(this.#writeLockKey));

    let result: Heads | undefined;
    if (this.doc) {
      if (options) {
        const { newDoc, newHeads } = A.changeAt(this.doc!, heads, options, callback);
        this.doc = newDoc;
        result = newHeads ?? undefined;
      } else {
        const { newDoc, newHeads } = A.changeAt(this.doc!, heads, callback);
        this.doc = newDoc;
        result = newHeads ?? undefined;
      }

      // No change event is emitted here since we are not using the doc handle. Notify listeners manually.
      this.notifyUpdate();
    } else {
      const handle = this.handle;
      invariant(handle);
      // No manual notification: the DB already processes the `change` event.
      result = this.#writeAndRefresh(() => handle.changeAt(heads, callback, options));
    }

    return result;
  }

  getDocAccessor(path: Doc.KeyPath = []): Doc.Accessor {
    assertArgument(Doc.isKeyPath(path), 'path');
    const self = this;
    return {
      handle: {
        doc: () => this.getDoc(),
        change: (callback, options) => {
          this.change(callback, options);
        },
        changeAt: (heads, callback, options) => {
          return this.changeAt(heads, callback, options);
        },
        addListener: (event, listener) => {
          if (event === 'change') {
            // TODO(dmaretskyi): We probably don't need to subscribe to docHandle here separately.
            this.handle?.on('change', listener);
            this.updates.on(listener);
            this.#accessorListeners++;
          }
        },
        removeListener: (event, listener) => {
          if (event === 'change') {
            // TODO(dmaretskyi): We probably don't need to subscribe to docHandle here separately.
            this.handle?.off('change', listener);
            this.updates.off(listener);
            this.#accessorListeners = Math.max(0, this.#accessorListeners - 1);
          }
        },
      },
      get path() {
        return [...self.mountPath, 'data', ...path];
      },
    };
  }

  /**
   * Fire a synchronous update notification via signal and event subscriptions.
   * Called after local changes and link resolution.
   * This function can be used unbound.
   */
  public readonly notifyUpdate = () => {
    // Refresh before the emit, so a subscriber reading the object inside its callback sees fresh values;
    // guarded separately from it, so a refresh that fails still lets subscribers hear about the change.
    this.#reportingErrors(() => this.#refresh());
    this.#reportingErrors(() => this.updates.emit());
  };

  #reportingErrors(fn: () => void): void {
    try {
      fn();
    } catch (err: any) {
      // Print the error message synchronously for easier debugging.
      // The stack trace and details will be printed asynchronously.
      log.catch(err);

      // Reports all errors that happen during even propagation as unhandled.
      // This is important since we don't want to silently swallow errors.
      // Unfortunately, this will only report errors in the next microtask after the current stack has already unwound.
      // TODO(dmaretskyi): Take some inspiration from facebook/react/packages/shared/invokeGuardedCallbackImpl.js
      throwUnhandledError(err);
    }
  }

  /**
   * Encode a value to be stored in the Automerge document.
   * Accepts arbitrary decoded input: in addition to {@link DecodedAutomergePrimaryValue}, this recursively
   * serializes structured values such as entity meta, whose annotation dictionary holds opaque
   * (schema-encoded) payloads that are not statically typed.
   */
  encode(value: unknown) {
    if (isProxy(value) as boolean) {
      throw new TypeError('Linking is not allowed');
    }

    if (value instanceof A.RawString) {
      return value;
    }
    if (value === undefined) {
      return null;
    }

    // EncodedReference values are already in the correct format for storage.
    if (isEncodedReference(value)) {
      return value;
    }
    if (value instanceof Uint8Array) {
      // Automerge stores Uint8Array natively; do not recurse into its byte indices.
      return value;
    }
    if (Array.isArray(value)) {
      const values: any = value.map((val) => this.encode(val));
      return values;
    }
    if (typeof value === 'object' && value !== null) {
      const entries = Object.entries(value).filter(([_, value]) => value !== undefined);
      return Object.fromEntries(entries.map(([key, value]): [string, any] => [key, this.encode(value)]));
    }

    if (typeof value === 'string' && value.length > STRING_CRDT_LIMIT) {
      return new A.RawString(value);
    }

    return value;
  }

  /**
   * Decode a value from the Automerge document.
   */
  decode(value: any): DecodedAutomergePrimaryValue {
    if (value === null) {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map((val) => this.decode(val));
    }
    if (value instanceof A.RawString) {
      return value.toString();
    }
    if (value instanceof Uint8Array) {
      return value;
    }
    // EncodedReference values are already in the correct format.
    if (isEncodedReference(value)) {
      return value;
    }
    if (typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([key, value]): [string, any] => [key, this.decode(value)]));
    }

    return value;
  }

  arrayPush(path: Doc.KeyPath, items: DecodedAutomergePrimaryValue[]): number {
    const itemsEncoded = items.map((item) => this.encode(item));

    let newLength: number = -1;
    this.change((doc) => {
      const fullPath = [...this.mountPath, ...path];
      const array = getDeep(doc, fullPath);
      invariant(Array.isArray(array));
      newLength = array.push(...itemsEncoded);
    });
    invariant(newLength !== -1);
    return newLength;
  }

  /**
   * The stored value at `path` as the document holds it, undecoded. The document is immutable, so the
   * result can be held for as long as the generation it was read at.
   */
  getRaw(path: Doc.KeyPath): AutomergeDoc<EntityStructure> | AutomergeDoc<DatabaseDirectory> {
    const fullPath = [...this.mountPath, ...path];

    let value = this.#root();
    for (const key of fullPath) {
      value = (value as any)?.[key];
    }

    return value;
  }

  private _setRaw(path: Doc.KeyPath, value: any): void {
    const fullPath = [...this.mountPath, ...path];

    this.change((doc) => {
      setDeep(doc, fullPath, value);
    });
  }

  // TODO(dmaretskyi): Rename to `get`.
  getDecoded(path: Doc.KeyPath): DecodedAutomergePrimaryValue {
    const decoded = this.decode(this.getRaw(path));
    return upgradeMeta(path, decoded) as DecodedAutomergePrimaryValue;
  }

  /**
   * Names of the data fields that changed between `heads` and the current frontier.
   *
   * Asks automerge which fields moved rather than comparing values, so it stays exact for nested
   * structures without needing a deep-equality rule of its own.
   */
  getChangedDataFieldsSince(heads: Heads): string[] {
    const doc: AutomergeDoc<unknown> | undefined = this.doc ?? this.docHandle?.doc();
    if (!doc) {
      return [];
    }

    const prefix = [...this.mountPath, DATA_NAMESPACE];
    const changed = new Set<string>();
    for (const patch of A.diff(doc, heads, A.getHeads(doc))) {
      if (patch.path.length > prefix.length && prefix.every((key, index) => patch.path[index] === key)) {
        changed.add(String(patch.path[prefix.length]));
      }
    }

    return [...changed];
  }

  // TODO(dmaretskyi): Rename to `set`.
  setDecoded(path: Doc.KeyPath, value: DecodedAutomergePrimaryValue): void {
    this._setRaw(path, this.encode(value));
  }

  /**
   * Deletes key at path.
   */
  delete(path: Doc.KeyPath): void {
    const fullPath = [...this.mountPath, ...path];

    this.change((doc) => {
      const value: any = getDeep(doc, fullPath.slice(0, fullPath.length - 1));
      delete value[fullPath[fullPath.length - 1]];
    });
  }

  getKind(): EntityKind {
    return (this.getRaw([SYSTEM_NAMESPACE, 'kind']) as any) ?? EntityKind.Object;
  }

  // TODO(dmaretskyi): Just set statically during construction.
  setKind(kind: EntityKind): void {
    this._setRaw([SYSTEM_NAMESPACE, 'kind'], kind);
  }

  getSource(): EncodedReference | undefined {
    const res = this.getRaw([SYSTEM_NAMESPACE, 'source']);
    if (!res || !EncodedReference.isEncodedReference(res)) {
      return undefined;
    }
    return res;
  }

  // TODO(dmaretskyi): Just set statically during construction.
  setSource(ref: EncodedReference): void {
    this._setRaw([SYSTEM_NAMESPACE, 'source'], ref);
  }

  getTarget(): EncodedReference | undefined {
    const res = this.getRaw([SYSTEM_NAMESPACE, 'target']);
    if (!res || !EncodedReference.isEncodedReference(res)) {
      return undefined;
    }
    return res;
  }

  // TODO(dmaretskyi): Just set statically during construction.
  setTarget(ref: EncodedReference): void {
    this._setRaw([SYSTEM_NAMESPACE, 'target'], ref);
  }

  getParent(): EncodedReference | undefined {
    const res = this.getRaw([SYSTEM_NAMESPACE, 'parent']);
    if (!res || !EncodedReference.isEncodedReference(res)) {
      return undefined;
    }
    return res;
  }

  setParent(ref: EncodedReference | undefined): void {
    if (ref === undefined) {
      this.delete([SYSTEM_NAMESPACE, 'parent']);
    } else {
      this._setRaw([SYSTEM_NAMESPACE, 'parent'], ref);
    }
  }

  /**
   * The entity this one was merged into, set on the loser of a convergence-key merge.
   * A bare id rather than an encoded reference, so reference-rewriting traversals do not mistake
   * the redirect for a data ref.
   */
  getMergedInto(): EntityId | undefined {
    const value = this.getRaw([SYSTEM_NAMESPACE, 'mergedInto']);
    return typeof value === 'string' ? value : undefined;
  }

  /**
   * Redirect the entity at `id` and record the heads it carried at that moment, so a later pass
   * can fold in edits made by a peer that was offline during the merge.
   */
  setMergedInto(id: EntityId, heads: readonly string[]): void {
    this._setRaw([SYSTEM_NAMESPACE, 'mergedInto'], id);
    this._setRaw([SYSTEM_NAMESPACE, 'mergedAtHeads'], [...heads]);
  }

  /**
   * The fold watermark: the stored `mergedAtHeads` unioned with every conflicting value of the
   * register. Peers merging concurrently each record their own watermark and automerge keeps one
   * plus conflicts; a fold diffing from the union never re-presents an edit any peer already
   * folded — a re-fold from a stale surviving watermark would overwrite newer winner edits.
   */
  getMergedAtHeads(): string[] | undefined {
    const value = this.getRaw([SYSTEM_NAMESPACE, 'mergedAtHeads']);
    if (!Array.isArray(value)) {
      return undefined;
    }
    const hashes = new Set<string>();
    const collect = (candidate: unknown): void => {
      if (Array.isArray(candidate)) {
        for (const hash of candidate) {
          if (typeof hash === 'string') {
            hashes.add(hash);
          }
        }
      }
    };
    collect(value);
    const doc = this.doc ?? this.docHandle?.doc();
    const system = doc
      ? getDeep<AutomergeDoc<{ mergedAtHeads?: string[] }>>(doc, [...this.mountPath, SYSTEM_NAMESPACE])
      : undefined;
    if (system !== undefined && typeof system === 'object') {
      for (const conflicting of Object.values(A.getConflicts(system, 'mergedAtHeads') ?? {})) {
        collect(conflicting);
      }
    }
    return [...hashes];
  }

  /**
   * Ids of the entities merged into this one, deduplicated and sorted.
   *
   * Peers merging concurrently on different views both append here, and automerge keeps both
   * insertions, so the stored list can hold repeats; normalizing on read makes it a set.
   */
  getMergedFrom(): EntityId[] {
    const value = this.getRaw([SYSTEM_NAMESPACE, 'mergedFrom']);
    return Array.isArray(value) ? [...new Set(value)].sort() : [];
  }

  /**
   * Record entities as having merged into this one. Idempotent: re-recording an id already
   * present rewrites nothing, so a repeated merge pass is a no-op.
   *
   * Appends to the existing list rather than assigning a new one: concurrent assignments are
   * whole-list conflicts and last-write-wins would drop one peer's ids, while concurrent inserts
   * both survive — {@link getMergedFrom} deduplicates.
   */
  addMergedFrom(ids: readonly EntityId[]): void {
    const existing = new Set(this.getMergedFrom());
    const missing = [...new Set(ids)].filter((id) => !existing.has(id)).sort();
    if (missing.length === 0) {
      return;
    }
    if (existing.size === 0 && this.getRaw([SYSTEM_NAMESPACE, 'mergedFrom']) === undefined) {
      this._setRaw([SYSTEM_NAMESPACE, 'mergedFrom'], missing);
    } else {
      this.arrayPush([SYSTEM_NAMESPACE, 'mergedFrom'], missing);
    }
  }

  /**
   * The document's current heads, recorded when the entity is merged away.
   */
  getHeads(): Heads {
    if (this.#snapshot) {
      return [...(this.#snapshot.handle.passiveHeads ?? [])];
    }
    const doc: AutomergeDoc<unknown> | undefined = this.doc ?? this.docHandle?.doc();
    return doc ? A.getHeads(doc) : [];
  }

  getType(): EncodedReference | undefined {
    const res = this.getRaw([SYSTEM_NAMESPACE, 'type']);
    if (!res || !EncodedReference.isEncodedReference(res)) {
      return undefined;
    }
    return res;
  }

  setType(ref: EncodedReference): void {
    this._setRaw([SYSTEM_NAMESPACE, 'type'], ref);
  }

  /**
   * Resolve a linked object held in the local link cache, keyed by entity id. Used to dereference
   * endpoint/parent references before the object is bound to a database.
   */
  lookupInLinkCache(ref: EncodedReference): Entity.Unknown | undefined {
    invariant(this.linkCache);
    const echoUri = EID.tryParse(EncodedReference.toURI(ref));
    const entityId = echoUri ? EID.getEntityId(echoUri) : undefined;
    invariant(entityId, 'Invalid DXN');
    return this.linkCache.get(entityId);
  }

  /**
   * Returns the Unix ms timestamp stored in system.createdAt, or undefined for objects
   * created before this field was introduced.
   */
  getCreatedAt(): number | undefined {
    const value = this.getRaw([SYSTEM_NAMESPACE, 'createdAt']);
    return typeof value === 'number' ? value : undefined;
  }

  /**
   * Returns the Unix ms timestamp of the last automerge change on this document,
   * or undefined when no change history is available.
   * Note: second-level precision (automerge change timestamps are Unix seconds).
   * Reads change metadata for the current heads (one backend lookup per head), not the
   * decoded change contents.
   */
  getUpdatedAt(): number | undefined {
    if (this.#snapshot) {
      return this.#snapshot.updatedAt;
    }
    const doc: AutomergeDoc<unknown> | undefined = this.doc ?? this.docHandle?.doc();
    if (!doc) {
      return undefined;
    }

    const heads = A.getHeads(doc);
    const headsKey = heads.join(',');
    const cached = updatedAtCache.get(doc);
    if (cached && cached.heads === headsKey) {
      return cached.updatedAt;
    }

    const backend = A.getBackend(doc);
    let maxTime = 0;
    for (const hash of heads) {
      const meta = backend.getChangeMetaByHash(hash);
      if (meta && meta.time > maxTime) {
        maxTime = meta.time;
      }
    }
    const updatedAt = maxTime > 0 ? maxTime * 1000 : undefined;
    updatedAtCache.set(doc, { heads: headsKey, updatedAt });
    return updatedAt;
  }

  getMeta(): EntityMeta {
    // Codec boundary: raw decoded automerge data is returned typed as the logical `EntityMeta`
    // (tags are encoded references at rest; the handler resolves them into tag entities on access).
    return this.getDecoded([META_NAMESPACE]) as unknown as EntityMeta;
  }

  /** References to the {@link Tag}s in `meta.tags`, as stored (a legacy bare id is upgraded on read). */
  getTags(): EncodedReference[] {
    const tags = this.getDecoded([META_NAMESPACE, 'tags']);
    return Array.isArray(tags) ? tags.filter(EncodedReference.isEncodedReference) : [];
  }

  setMeta(meta: EntityMeta): void {
    this._setRaw([META_NAMESPACE], this.encode(meta));
  }

  /**
   * Whether the entity is deleted, itself or through a strong dependency up to `remainingDepth` hops away.
   * A `memo` shares verdicts across one synchronous read, so siblings walk their common ancestors once.
   */
  isDeleted(remainingDepth: number = 10, memo?: DeletionMemo): boolean {
    const cached = memo?.get(this)?.[remainingDepth];
    if (cached !== undefined) {
      return cached;
    }
    const deleted = this.#computeIsDeleted(remainingDepth, memo);
    if (memo) {
      const byDepth = memo.get(this) ?? [];
      byDepth[remainingDepth] = deleted;
      memo.set(this, byDepth);
    }
    return deleted;
  }

  #computeIsDeleted(remainingDepth: number, memo: DeletionMemo | undefined): boolean {
    this.#readDeletionFields();
    if (this.#ownDeleted) {
      return true;
    }

    // An entity is transitively deleted when one of its strong dependencies is deleted: a child
    // when its parent is removed, or a relation when either endpoint is removed — a dangling
    // relation has no valid graph edge, so it is treated as deleted and excluded from queries.
    return (
      this.entityManager !== undefined &&
      remainingDepth > 0 &&
      (this.#isDependencyDeleted(this.#parentId, remainingDepth, memo) ||
        this.#isDependencyDeleted(this.#sourceId, remainingDepth, memo) ||
        this.#isDependencyDeleted(this.#targetId, remainingDepth, memo))
    );
  }

  /**
   * Adds to `ids` the same-space entities whose writes can change {@link isDeleted}: the strong dependencies it walks
   * and the merge redirects between them. Nothing is loaded.
   */
  addDeletionDependencies(ids: Set<string>, remainingDepth: number = 10): void {
    if (!this.entityManager || remainingDepth <= 0) {
      return;
    }
    this.#readDeletionFields();
    for (const dependency of [this.#parentId, this.#sourceId, this.#targetId]) {
      // An entity already in `ids` has its dependencies there, but a redirect from it may not be.
      let id = dependency;
      while (id !== undefined) {
        const seen = ids.has(id);
        ids.add(id);
        const core = this.entityManager.getObjectCoreById(id, LOOKUP_HELD);
        const next = core?.getMergedInto();
        if (next === undefined || next >= id) {
          if (!seen) {
            core?.addDeletionDependencies(ids, remainingDepth - 1);
          }
          break;
        }
        id = next;
      }
    }
  }

  #readDeletionFields(): void {
    const root = this.#root();
    if (root === this.#deletionRoot) {
      return;
    }
    this.#deletionRoot = root;
    const deleted: unknown = this.getRaw([SYSTEM_NAMESPACE, 'deleted']);
    this.#ownDeleted = deleted === true;
    this.#parentId = this.#sameSpaceEntityId(this.getParent());
    const relation = this.getKind() === EntityKind.Relation;
    this.#sourceId = relation ? this.#sameSpaceEntityId(this.getSource()) : undefined;
    this.#targetId = relation ? this.#sameSpaceEntityId(this.getTarget()) : undefined;
    this.#mergedIntoId = this.getMergedInto();
  }

  /** The entity `ref` points at, if it is in this core's space; cross-space and unparseable refs have none. */
  #sameSpaceEntityId(ref: EncodedReference | undefined): EntityId | undefined {
    if (!ref || !this.entityManager) {
      return undefined;
    }
    const echoUri = EID.tryParse(EncodedReference.toURI(ref));
    const spaceId = echoUri ? EID.getSpaceId(echoUri) : undefined;
    const entityId = echoUri ? EID.getEntityId(echoUri) : undefined;
    if (!entityId || (spaceId !== undefined && spaceId !== this.entityManager.spaceId)) {
      return undefined;
    }
    return entityId;
  }

  get #dependencyLookup(): GetObjectCoreByIdOptions {
    return this.#snapshot === undefined ? LOOKUP_LOADING : LOOKUP_HELD;
  }

  /**
   * Whether the same-space entity `entityId` is (transitively) deleted; an unresolved one is treated as
   * not-deleted. Strong dependencies guarantee parent/relation endpoints load alongside the dependent
   * entity, so this stays a synchronous core lookup — `loadObjectCoreById` is avoided because it may be async.
   *
   * A merged-away dependency is a renamed one, not a removed one: the walk follows `mergedInto`
   * redirects and judges deletion at the survivor, so relations and children anchored at a merge
   * loser stay visible while the survivor lives.
   */
  #isDependencyDeleted(
    entityId: EntityId | undefined,
    remainingDepth: number,
    memo: DeletionMemo | undefined,
  ): boolean {
    if (entityId === undefined) {
      return false;
    }
    const core = this._resolveMergeRedirect(this.entityManager?.getObjectCoreById(entityId, this.#dependencyLookup));
    return core != null && core.isDeleted(remainingDepth - 1, memo);
  }

  /**
   * Follow `mergedInto` redirects to the live end of the chain. Edges must strictly decrease the
   * id — a non-decreasing edge is corrupt data and terminates the walk at the current entity,
   * matching `resolveMergeRedirect` in `@dxos/echo/internal`. A hop whose core is not loaded returns `undefined`: the
   * survivor cannot be judged synchronously, and an unresolvable dependency is treated as live.
   */
  private _resolveMergeRedirect(core: ObjectCore | undefined): ObjectCore | undefined {
    let current = core;
    while (current) {
      current.#readDeletionFields();
      const next = current.#mergedIntoId;
      if (next === undefined || next >= current.id) {
        return current;
      }
      current = this.entityManager?.getObjectCoreById(next, this.#dependencyLookup);
    }
    return undefined;
  }

  setDeleted(value: boolean): void {
    this._setRaw([SYSTEM_NAMESPACE, 'deleted'], value);
  }

  /**
   * URIs of the entities that this entity strongly depends on: the schema reference (when stored as
   * an object), source/target for relations, and the parent ref. Strong dependencies are loaded
   * together with the depending entity before it is surfaced. URIs may be cross-space `echo:` EIDs
   * or queue-item EIDs; the resolver routes each to the appropriate backend.
   */
  getStrongDependencies(): URI.URI[] {
    const typeRef = this.getType();
    const sourceRef = this.getSource();
    const targetRef = this.getTarget();
    const parentRef = this.getParent();
    return getStrongDependencyUris(
      {
        kind: this.getKind(),
        type: typeRef ? EncodedReference.toURI(typeRef) : undefined,
        source: sourceRef ? EncodedReference.toURI(sourceRef) : undefined,
        target: targetRef ? EncodedReference.toURI(targetRef) : undefined,
        parent: parentRef ? EncodedReference.toURI(parentRef) : undefined,
        tags: this.getTags().map((ref) => EncodedReference.toURI(ref)),
      },
      this.entityManager?.spaceId,
    );
  }
}

export type BindOptions = {
  db: IDatabaseBinding;
  docHandle: DocHandleProxy<DatabaseDirectory>;
  path: Doc.KeyPath;
  /** Assign the state from the local doc into the shared structure for the database. */
  assignFromLocalState?: boolean;
};

export const objectIsUpdated = (objId: string, event: DocHandleChangePayload<DatabaseDirectory>) => {
  if (event.patches.some((patch) => patch.path[0] === 'objects' && patch.path[1] === objId)) {
    return true;
  }
  return false;
};

/**
 * Lazily normalizes `meta` on read so the now-required `tags`/`annotations` fields are always present,
 * and upgrades bare string entries in `meta.tags` to {@link EncodedReference}s so they materialize as
 * `Ref<Tag>` like any other reference. The transform happens on read and persists physically only on the
 * next write. This keeps data written before these fields existed (or before tags became refs) readable
 * without an eager migration. Scoped strictly to the `meta` namespace so unrelated values in `data` are
 * untouched.
 */
const dedupeForeignKeys = (keys: readonly unknown[]): unknown[] => {
  const seen = new Set<string>();
  return keys.filter((key) => {
    const { source, id } = (key ?? {}) as { source?: string; id?: string };
    // JSON-composite rather than concatenation, so a delimiter inside one part cannot collide.
    const composite = JSON.stringify([source, id]);
    if (seen.has(composite)) {
      return false;
    }
    seen.add(composite);
    return true;
  });
};

const upgradeMeta = (path: Doc.KeyPath, value: unknown): unknown => {
  if (path[0] !== META_NAMESPACE) {
    return value;
  }
  // Whole `meta` object: backfill required fields, upgrade tag ids, deduplicate keys.
  if (path.length === 1 && value != null && typeof value === 'object') {
    const meta = value as Record<string, unknown>;
    return {
      ...meta,
      keys: Array.isArray(meta.keys) ? dedupeForeignKeys(meta.keys) : meta.keys,
      tags: Array.isArray(meta.tags) ? meta.tags.map(upgradeTagRef) : [],
      annotations: meta.annotations ?? {},
    };
  }
  // `meta.keys`: peers merging concurrently both append the same missing key, and automerge keeps
  // both insertions — normalizing on read makes the list a set.
  if (path.length === 2 && path[1] === 'keys') {
    return Array.isArray(value) ? dedupeForeignKeys(value) : value;
  }
  // `meta.tags`: default to an empty array when absent; upgrade string entries.
  if (path.length === 2 && path[1] === 'tags') {
    return Array.isArray(value) ? value.map(upgradeTagRef) : [];
  }
  // `meta.annotations`: default to an empty dictionary when absent.
  if (path.length === 2 && path[1] === 'annotations') {
    return value ?? {};
  }
  // A single `meta.tags[i]` element.
  if (path.length === 3 && path[1] === 'tags') {
    return upgradeTagRef(value);
  }
  return value;
};

const upgradeTagRef = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }
  // A bare tag id string materializes as a `Ref<Tag>` like any other reference.
  const uri = EID.tryParse(value) ?? value;
  return EncodedReference.fromURI(URI.make(uri));
};
